import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import ReactDOM from "react-dom";
import type { TreeNode } from "primereact/treenode";
import { Button } from "../Button";
import { Search } from "../Search";
import type { AppliedFilter, SearchProps } from "../Search/Search";
import { getAllStatusIcons } from "../Status/status.config";
import { NavLink } from "../NavLink";
import type { NavLinkType } from "../NavLink/NavLink";
import { Dropdown } from "../AuditFramework/Dropdown";
import type {
  DockFlyoutFilterSection,
  DockFlyoutFiltersState,
  DockFlyoutListItem,
  DockButtonConfig,
  DockFlyoutConfig,
  DockFlyoutHeaderAction,
  DockFlyoutRowAction,
} from "../../types/dockFlyoutDataTypes";
import { DockFlyoutSecondaryPanel } from "./DockFlyoutSecondaryPanel";

type RowOptionAction =
  | string
  | {
    id: string;
    label?: string;
  };

interface GroupedRenderHintsInput {
  groupHeaderIds: string[];
  collapsedGroupIds: string[];
  query: string;
}

interface GroupedRenderHints {
  defaultCollapsedGroupIds?: string[];
  pinnedFooterGroupIds?: string[];
  collapseOthersOnExpand?: boolean;
}

type DockConfirmKind = "bulk-unpinned" | "multi-delete" | "single";

interface DockConfirmState {
  kind: DockConfirmKind;
  targetId?: string;
}

interface DockRenameDialogState {
  targetId: string;
  currentName: string;
  draftName: string;
}

interface DockFlyoutController {
  isOpen: boolean;
  activeDockButton: DockButtonConfig | null;
  anchorRect: DOMRect | null;
  secondaryAnchorRect: DOMRect | null;
  flyoutConfig: DockFlyoutConfig | undefined;
  flyoutTitle: string;
  closeFlyout: () => void;

  query: string;
  selectedScope: string;
  appliedFilters: DockFlyoutFiltersState;
  setQuery: (value: string) => void;
  clearQuery: () => void;
  setSelectedScope: (value: string) => void;

  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  loadNextPage: () => void;

  listItems: DockFlyoutListItem[];
  pinnedIds?: readonly string[];

  dropdownOptions: Array<{ id: string; label: string; isActive: boolean }>;

  filterSections: DockFlyoutFilterSection[];
  resetDraftFilters: () => void;
  toggleFilterValue: (sectionId: string, value: string) => void;
  applyDraftFilters: () => void;

  openActionMenuId: string | null;
  closeRowMenu: () => void;
  navigateToItem: (item: DockFlyoutListItem) => void;

  activeSecondaryItem: DockFlyoutListItem | null;
  openSecondaryForRow: (itemId: string, anchorRect: DOMRect | null) => void;
  closeSecondary: () => void;
  openSecondaryGroupId: string | null;
  setOpenSecondaryGroupId: (groupId: string | null) => void;
  syncWorkspaceObjectives?: () => void | Promise<void>;

  executeHeaderAction: (
    action: DockFlyoutHeaderAction,
    optionId?: string,
  ) => void;

  selectedIds?: readonly string[];
  toggleSelection?: (itemId: string) => void;
  extendSelectionTo?: (itemId: string) => void;
  clearSelection?: () => void;
  exportSelection?: () => void;
  openConfirm?: (confirm: DockConfirmState) => void;
  confirm?: DockConfirmState | null;
  closeConfirm?: () => void;
  confirmDelete?: () => void;

  renameDialog?: DockRenameDialogState | null;
  setRenameDraftName?: (name: string) => void;
  closeRenameDialog?: () => void;
  submitRename?: () => void;
  isRenaming?: boolean;

  executeRowAction?: (
    itemId: string,
    action: DockFlyoutRowAction | RowOptionAction,
  ) => void;

  getGroupedRenderHints?: (
    input: GroupedRenderHintsInput,
  ) => GroupedRenderHints;
}

const VALID_STATUS_ICON_SET = new Set(getAllStatusIcons());

interface RowOptionsState {
  item: DockFlyoutListItem;
  actions: RowOptionAction[];
  anchorRect: DOMRect;
}

function getGroupId(item: DockFlyoutListItem): string | null {
  const maybeGroupId = item.filters?.__groupId;

  return typeof maybeGroupId === "string" && maybeGroupId.trim().length > 0
    ? maybeGroupId.trim()
    : null;
}

function isGroupHeaderItem(item: DockFlyoutListItem): boolean {
  return item.isAggregate === true && item.id.startsWith("__group__");
}

function getBooleanField(
  item: DockFlyoutListItem,
  fieldName: string,
): boolean {
  return (item as unknown as Record<string, unknown>)[fieldName] === true;
}

function getNumberField(item: DockFlyoutListItem, fieldName: string): number {
  const value = (item as unknown as Record<string, unknown>)[fieldName];

  return typeof value === "number" ? value : 0;
}

function isBasePinnedItem(
  item: DockFlyoutListItem,
  pinnedIds: readonly string[] = [],
): boolean {
  return (
    pinnedIds.includes(item.id) ||
    getBooleanField(item, "isPinned") ||
    getBooleanField(item, "pinned") ||
    getBooleanField(item, "is_pinned")
  );
}

function isPinnedItem(
  item: DockFlyoutListItem,
  pinnedIds: readonly string[] = [],
  pinOverrides: Record<string, boolean> = {},
): boolean {
  const override = pinOverrides[item.id];

  if (typeof override === "boolean") {
    return override;
  }

  return isBasePinnedItem(item, pinnedIds);
}

function isUnreadItem(item: DockFlyoutListItem): boolean {
  return (
    getBooleanField(item, "isUnread") ||
    getBooleanField(item, "unread") ||
    getBooleanField(item, "hasUnread") ||
    getNumberField(item, "unreadCount") > 0 ||
    getNumberField(item, "notificationCount") > 0
  );
}

function getValidStatusIconName(option: {
  id: string;
  label: string;
}): string | null {
  if (VALID_STATUS_ICON_SET.has(option.id as any)) {
    return option.id;
  }

  if (VALID_STATUS_ICON_SET.has(option.label as any)) {
    return option.label;
  }

  return null;
}

function getRowActions(item: DockFlyoutListItem): RowOptionAction[] {
  const actions = (item as any)?.optionsMenu?.actions;

  if (!Array.isArray(actions)) {
    return [];
  }

  return actions.filter(
    (action): action is RowOptionAction =>
      typeof action === "string" ||
      (typeof action === "object" &&
        action !== null &&
        typeof action.id === "string"),
  );
}

function getRowActionId(rowAction: RowOptionAction): string {
  return typeof rowAction === "string" ? rowAction : rowAction.id;
}

function getRowActionLabel(rowAction: RowOptionAction): string {
  if (typeof rowAction !== "string" && rowAction.label) {
    return rowAction.label;
  }

  const rowActionId = getRowActionId(rowAction);

  switch (rowActionId) {
    case "pin":
      return "Pin";
    case "unpin":
      return "Unpin";
    case "edit":
      return "Edit";
    case "share":
      return "Share";
    case "delete":
      return "Delete";
    default:
      return rowActionId;
  }
}

function getRowActionIcon(rowAction: RowOptionAction): string {
  switch (getRowActionId(rowAction)) {
    case "pin":
    case "unpin":
      return "Push Pin";
    case "edit":
      return "Edit";
    case "share":
      return "Share";
    case "delete":
      return "Delete";
    default:
      return "Add";
  }
}

function hasRowOptions(item: DockFlyoutListItem): boolean {
  const optionsMenu = (item as any).optionsMenu;

  return (
    optionsMenu?.show === true &&
    Array.isArray(optionsMenu.actions) &&
    optionsMenu.actions.length > 0
  );
}

function getDisplayRowAction(
  rowAction: RowOptionAction,
  item: DockFlyoutListItem,
  pinnedIds: readonly string[],
  pinOverrides: Record<string, boolean>,
): RowOptionAction {
  const rowActionId = getRowActionId(rowAction);

  if (rowActionId !== "pin" && rowActionId !== "unpin") {
    return rowAction;
  }

  const pinned = isPinnedItem(item, pinnedIds, pinOverrides);

  return {
    id: pinned ? "unpin" : "pin",
    label: pinned ? "Unpin" : "Pin",
  };
}

function buildSearchFilters(
  filterSections: DockFlyoutFilterSection[],
  appliedFilters: DockFlyoutFiltersState,
): NonNullable<SearchProps["filters"]> {
  return filterSections.map((section) => ({
    label: section.label,
    type: "checkbox" as const,
    showInSearch: false,
    model: (appliedFilters[section.id] ?? []).filter((value) =>
      VALID_STATUS_ICON_SET.has(value as any),
    ),
    optionLabel: "name",
    options: section.options
      .map((option) => {
        const statusIconName = getValidStatusIconName(option);

        if (!statusIconName) {
          return null;
        }

        return {
          name: statusIconName,
          code: option.id,
        };
      })
      .filter(
        (option): option is { name: string; code: string } => option !== null,
      ),
  }));
}

function getFlyoutPositionStyle(
  anchorRect: DOMRect | null,
  panelHeight: number,
): CSSProperties {
  if (!anchorRect || typeof window === "undefined") {
    return {};
  }

  const viewportWidth = window.innerWidth;
  // const viewportHeight = window.innerHeight;
  const gutter = 12;
  const preferRight = anchorRect.left < viewportWidth / 2;

  const top = 88;
  void panelHeight;

  if (preferRight) {
    return {
      top: `${top}px`,
      left: `${anchorRect.right + gutter}px`,
      right: "auto",
    };
  }

  return {
    top: `${top}px`,
    right: `${Math.max(viewportWidth - anchorRect.left + gutter, gutter)}px`,
    left: "auto",
  };
}

function getSecondaryFlyoutPositionStyle(
  anchorRect: DOMRect | null,
  primaryRect: DOMRect | null,
  secondaryPanelHeight: number,
  secondaryPanelWidth: number,
): CSSProperties {
  if (!anchorRect || typeof window === "undefined") {
    return {};
  }

  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const viewportGap = 12;
  const overlapX = 14;
  const rowTopOffset = 8;
  const panelWidth = Math.max(secondaryPanelWidth, 320);

  const maxUsableHeight = Math.max(0, viewportHeight - viewportGap * 2);
  const measuredPanelHeight = secondaryPanelHeight > 0 ? secondaryPanelHeight : 320;
  const desiredPanelHeight = Math.min(measuredPanelHeight, maxUsableHeight);
  const dockFlyoutHeight = primaryRect?.height ?? maxUsableHeight;
  const minimumReachableHeight = Math.min(
    Math.round(dockFlyoutHeight * 0.95),
    maxUsableHeight,
    desiredPanelHeight,
  );

  // Keep the secondary panel visually tied to the clicked main-menu row.
  // If the full panel cannot fit below that row, constrain its max-height and
  // only move it up enough to expose a usable, scrollable panel area.
  let top = Math.max(anchorRect.top - rowTopOffset, viewportGap);
  const maxTopForReachablePanel = Math.max(
    viewportGap,
    viewportHeight - viewportGap - minimumReachableHeight,
  );

  top = Math.min(top, maxTopForReachablePanel);

  const maxPanelHeight = Math.max(0, viewportHeight - top - viewportGap);
  const preferredLeft = anchorRect.right - overlapX;

  if (preferredLeft + panelWidth <= viewportWidth - viewportGap) {
    return {
      top: `${top}px`,
      left: `${preferredLeft}px`,
      right: "auto",
      maxHeight: `${maxPanelHeight}px`,
    };
  }

  const primaryPanelRight = primaryRect?.right ?? 0;
  const minLeftWithoutOverlap = primaryPanelRight - overlapX;
  const leftPosition = anchorRect.left - panelWidth + overlapX;

  if (leftPosition < minLeftWithoutOverlap) {
    return {
      top: `${top}px`,
      left: `${minLeftWithoutOverlap}px`,
      right: "auto",
      maxHeight: `${maxPanelHeight}px`,
    };
  }

  return {
    top: `${top}px`,
    left: `${leftPosition}px`,
    right: "auto",
    maxHeight: `${maxPanelHeight}px`,
  };
}

function getRowOptionsMenuPositionStyle(
  anchorRect: DOMRect | null,
  actionCount: number,
): CSSProperties {
  if (!anchorRect || typeof window === "undefined") {
    return {};
  }

  const menuWidth = 180;
  const gutter = 8;
  const itemHeight = 36;
  const menuPadding = 8;
  const menuGapTotal = Math.max(actionCount - 1, 0) * 2;
  const estimatedMenuHeight =
    Math.max(actionCount, 1) * itemHeight + menuPadding + menuGapTotal;

  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  const left = Math.max(
    gutter,
    Math.min(anchorRect.right - menuWidth, viewportWidth - menuWidth - gutter),
  );

  const spaceBelow = viewportHeight - anchorRect.bottom - gutter;
  const spaceAbove = anchorRect.top - gutter;

  let top: number;

  if (spaceBelow >= estimatedMenuHeight || spaceBelow >= spaceAbove) {
    top = anchorRect.bottom + gutter;
  } else {
    top = anchorRect.top - estimatedMenuHeight - gutter;
  }

  top = Math.max(
    gutter,
    Math.min(top, viewportHeight - estimatedMenuHeight - gutter),
  );

  return {
    position: "fixed",
    top: `${top}px`,
    left: `${left}px`,
    width: `${menuWidth}px`,
    zIndex: 999999,
  };
}

export interface DockFlyoutProps {
  controller: DockFlyoutController;
}

export const DockFlyout: React.FC<DockFlyoutProps> = ({ controller }) => {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const secondaryPanelRef = useRef<HTMLDivElement | null>(null);
  const searchHostRef = useRef<HTMLDivElement | null>(null);
  const filterIconBtnRef = useRef<HTMLSpanElement | null>(null);
  const rowOptionsMenuRef = useRef<HTMLDivElement | null>(null);

  const [isSecondaryLocked, setIsSecondaryLocked] = useState(false);
  const [collapsedGroupIds, setCollapsedGroupIds] = useState<string[]>([]);
  const [secondaryPanelHeight, setSecondaryPanelHeight] = useState(0);
  const [primaryPanelRect, setPrimaryPanelRect] = useState<DOMRect | null>(
    null,
  );
  const [secondaryPanelWidth, setSecondaryPanelWidth] = useState(0);
  const [rowOptionsState, setRowOptionsState] =
    useState<RowOptionsState | null>(null);
  const wasOpenRef = useRef(false);
  const initialCollapseAppliedRef = useRef(false);

  const [pinOverrides, setPinOverrides] = useState<Record<string, boolean>>({});

  const {
    isOpen,
    activeDockButton,
    anchorRect,
    secondaryAnchorRect,
    flyoutConfig,
    flyoutTitle,
    closeFlyout,
    query,
    selectedScope,
    appliedFilters,
    setQuery,
    clearQuery,
    setSelectedScope,
    isLoading,
    isLoadingMore,
    hasMore,
    loadNextPage,
    listItems,
    pinnedIds = [],
    dropdownOptions,
    filterSections,
    resetDraftFilters,
    toggleFilterValue,
    applyDraftFilters,
    openActionMenuId,
    closeRowMenu,
    navigateToItem,
    activeSecondaryItem,
    openSecondaryForRow,
    closeSecondary,
    openSecondaryGroupId,
    setOpenSecondaryGroupId,
    syncWorkspaceObjectives,
    executeHeaderAction,
    executeRowAction,
    selectedIds = [],
    toggleSelection,
    extendSelectionTo,
    clearSelection,
    exportSelection,
    openConfirm,
    confirm = null,
    closeConfirm,
    confirmDelete,
    renameDialog = null,
    setRenameDraftName,
    closeRenameDialog,
    submitRename,
    isRenaming = false,
    getGroupedRenderHints,
  } = controller;

  const flyoutKind = activeDockButton?.kind;

  const rowOptionsItem = rowOptionsState?.item ?? null;
  const rowOptionsActions = rowOptionsState?.actions ?? [];
  const rowOptionsAnchorRect = rowOptionsState?.anchorRect ?? null;

  useEffect(() => {
    setPinOverrides((prev) => {
      let changed = false;
      const next: Record<string, boolean> = {};

      listItems.forEach((item) => {
        const override = prev[item.id];

        if (typeof override !== "boolean") {
          return;
        }

        const basePinned = isBasePinnedItem(item, pinnedIds);

        if (basePinned !== override) {
          next[item.id] = override;
        } else {
          changed = true;
        }
      });

      const prevKeys = Object.keys(prev);

      if (!changed && prevKeys.length === Object.keys(next).length) {
        return prev;
      }

      return next;
    });
  }, [listItems, pinnedIds]);

  const closeRowOptionsMenu = useCallback(() => {
    setRowOptionsState(null);
  }, []);

  const handleRowActionClick = (
    rowAction: RowOptionAction,
    item: DockFlyoutListItem,
  ) => {
    const displayAction = getDisplayRowAction(
      rowAction,
      item,
      pinnedIds,
      pinOverrides,
    );
    const rowActionId = getRowActionId(displayAction);

    if (rowActionId === "pin" || rowActionId === "unpin") {
      setPinOverrides((prev) => ({
        ...prev,
        [item.id]: rowActionId === "pin",
      }));
    }

    executeRowAction?.(item.id, displayAction);

    closeRowOptionsMenu();
  };

  const getRowActionAnchorRect = useCallback((itemId: string): DOMRect | null => {
    const rows = Array.from(
      panelRef.current?.querySelectorAll<HTMLElement>(
        '[data-navlink-row="true"]',
      ) ?? [],
    );

    const row = rows.find(
      (rowElement) => rowElement.getAttribute("data-navlink-key") === itemId,
    );

    if (!row) {
      return null;
    }

    const rightContent = row.querySelector<HTMLElement>(
      ".dock-flyout__nav-row-right",
    );
    const actionButton = rightContent?.querySelector<HTMLElement>("button");

    return actionButton?.getBoundingClientRect() ?? row.getBoundingClientRect();
  }, []);

  const openRowOptionsMenu = useCallback(
    (item: DockFlyoutListItem) => {
      if (rowOptionsItem?.id === item.id) {
        closeRowOptionsMenu();
        return;
      }

      const actions = getRowActions(item);
      const anchorRectForMenu = getRowActionAnchorRect(item.id);

      if (!anchorRectForMenu) {
        return;
      }

      closeSecondary();
      setIsSecondaryLocked(false);
      setRowOptionsState({
        item,
        actions,
        anchorRect: anchorRectForMenu,
      });
    },
    [
      rowOptionsItem,
      closeRowOptionsMenu,
      getRowActionAnchorRect,
      closeSecondary,
    ],
  );

  useEffect(() => {
    if (!activeSecondaryItem || !secondaryPanelRef.current) {
      setSecondaryPanelWidth(0);
      setSecondaryPanelHeight(0);
      return;
    }

    const secondaryElement = secondaryPanelRef.current;

    const updateSecondarySize = () => {
      const rect = secondaryElement.getBoundingClientRect();
      const contentHeight = Math.max(secondaryElement.scrollHeight, rect.height);

      setSecondaryPanelWidth(rect.width);
      setSecondaryPanelHeight(contentHeight);
    };

    updateSecondarySize();

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(updateSecondarySize);
      observer.observe(secondaryElement);

      return () => observer.disconnect();
    }

    window.addEventListener("resize", updateSecondarySize);
    return () => window.removeEventListener("resize", updateSecondarySize);
  }, [activeSecondaryItem, openSecondaryGroupId]);

  useEffect(() => {
    if (!isOpen) {
      setPrimaryPanelRect(null);
      closeRowOptionsMenu();
      return;
    }

    const panelElement = panelRef.current;

    if (!panelElement) {
      return;
    }

    const updatePrimaryRect = () => {
      const rect = panelElement.getBoundingClientRect();
      setPrimaryPanelRect(new DOMRect(rect.x, rect.y, rect.width, rect.height));
    };

    updatePrimaryRect();

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(updatePrimaryRect);
      observer.observe(panelElement);

      window.addEventListener("resize", updatePrimaryRect);

      return () => {
        observer.disconnect();
        window.removeEventListener("resize", updatePrimaryRect);
      };
    }

    window.addEventListener("resize", updatePrimaryRect);
    return () => window.removeEventListener("resize", updatePrimaryRect);
  }, [isOpen, closeRowOptionsMenu]);

  useEffect(() => {
    if (!activeSecondaryItem) {
      setIsSecondaryLocked(false);
    }
  }, [activeSecondaryItem]);

  useEffect(() => {
    closeRowOptionsMenu();
  }, [query, selectedScope, appliedFilters, closeRowOptionsMenu]);

  useEffect(() => {
    if (!isOpen) {
      setIsSecondaryLocked(false);
      closeRowOptionsMenu();
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") {
        return;
      }

      if (confirm) {
        closeConfirm?.();
        return;
      }

      if (rowOptionsItem) {
        closeRowOptionsMenu();
      } else if (openActionMenuId) {
        closeRowMenu();
      } else if (activeSecondaryItem) {
        setIsSecondaryLocked(false);
        closeSecondary();
      } else {
        setIsSecondaryLocked(false);
        closeFlyout();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [
    isOpen,
    rowOptionsItem,
    confirm,
    closeConfirm,
    openActionMenuId,
    activeSecondaryItem,
    closeRowMenu,
    closeSecondary,
    closeFlyout,
    closeRowOptionsMenu,
  ]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as Node | null;

      if (!target) {
        return;
      }

      const isInsidePrimary = panelRef.current?.contains(target);
      const isInsideSecondary = secondaryPanelRef.current?.contains(target);
      const isInsideRowOptions = rowOptionsMenuRef.current?.contains(target);

      const isCurrentRowOptionsTrigger =
        rowOptionsItem &&
        target instanceof Element &&
        target.closest('[data-navlink-row="true"]')?.getAttribute(
          "data-navlink-key",
        ) === rowOptionsItem.id &&
        !!target.closest("button");

      if (
        rowOptionsItem &&
        !isInsideRowOptions &&
        !isCurrentRowOptionsTrigger
      ) {
        closeRowOptionsMenu();
      }

      if (!isInsidePrimary && !isInsideSecondary && !isInsideRowOptions) {
        setIsSecondaryLocked(false);
        closeRowOptionsMenu();
        closeFlyout();
        return;
      }

      if (
        openActionMenuId &&
        target instanceof Element &&
        !target.closest(".dock-flyout__row-actions")
      ) {
        closeRowMenu();
      }
    };

    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [
    isOpen,
    rowOptionsItem,
    openActionMenuId,
    closeFlyout,
    closeRowMenu,
    closeRowOptionsMenu,
  ]);

  useEffect(() => {
    const bodyEl = bodyRef.current;

    if (!isOpen || !bodyEl) {
      return;
    }

    const handleScroll = () => {
      if (rowOptionsItem) {
        closeRowOptionsMenu();
      }

      if (isLoading || isLoadingMore || !hasMore) {
        return;
      }

      const threshold = 48;
      const distanceFromBottom =
        bodyEl.scrollHeight - bodyEl.scrollTop - bodyEl.clientHeight;

      if (distanceFromBottom <= threshold) {
        loadNextPage();
      }
    };

    bodyEl.addEventListener("scroll", handleScroll);
    return () => bodyEl.removeEventListener("scroll", handleScroll);
  }, [
    isOpen,
    isLoading,
    isLoadingMore,
    hasMore,
    loadNextPage,
    rowOptionsItem,
    closeRowOptionsMenu,
  ]);

  const positionStyle = useMemo(
    () => getFlyoutPositionStyle(anchorRect, primaryPanelRect?.height ?? 0),
    [anchorRect, primaryPanelRect?.height],
  );

  const secondaryPositionStyle = useMemo(
    () =>
      getSecondaryFlyoutPositionStyle(
        secondaryAnchorRect,
        primaryPanelRect,
        secondaryPanelHeight,
        secondaryPanelWidth,
      ),
    [
      secondaryAnchorRect,
      primaryPanelRect,
      secondaryPanelHeight,
      secondaryPanelWidth,
    ],
  );

  const rowOptionsMenuStyle = useMemo(
    () =>
      getRowOptionsMenuPositionStyle(
        rowOptionsAnchorRect,
        rowOptionsActions.length,
      ),
    [rowOptionsAnchorRect, rowOptionsActions.length],
  );

  const searchFilters = useMemo(
    () => buildSearchFilters(filterSections, appliedFilters),
    [filterSections, appliedFilters],
  );

  const flyoutStructure = flyoutConfig?.structure;

  const sortedListItems = useMemo(() => {
    if (listItems.some((item) => item.isAggregate)) {
      return listItems;
    }

    return [...listItems].sort((firstItem, secondItem) => {
      const firstPinned = isPinnedItem(firstItem, pinnedIds, pinOverrides);
      const secondPinned = isPinnedItem(secondItem, pinnedIds, pinOverrides);

      if (firstPinned === secondPinned) {
        return 0;
      }

      return firstPinned ? -1 : 1;
    });
  }, [listItems, pinnedIds, pinOverrides]);

  const groupedListItems = useMemo(() => {
    if (flyoutStructure !== "grouped") {
      return sortedListItems;
    }

    const hasGroupHeaders = sortedListItems.some((item) => isGroupHeaderItem(item));
    if (!hasGroupHeaders) {
      return sortedListItems;
    }

    const normalizedQuery = query.trim().toLowerCase();
    const useCollapseState = normalizedQuery.length === 0;

    const result: DockFlyoutListItem[] = [];
    let currentHeader: DockFlyoutListItem | null = null;
    let currentItems: DockFlyoutListItem[] = [];

    const flushGroup = () => {
      if (!currentHeader) {
        return;
      }

      const groupId = getGroupId(currentHeader) ?? currentHeader.id;
      const isCollapsed = useCollapseState && collapsedGroupIds.includes(groupId);

      let visibleItems = currentItems;
      if (normalizedQuery) {
        visibleItems = currentItems.filter(
          (item) =>
            item.name.toLowerCase().includes(normalizedQuery) ||
            item.subtitle?.toLowerCase().includes(normalizedQuery),
        );
      }

      if (normalizedQuery && visibleItems.length === 0) {
        currentHeader = null;
        currentItems = [];
        return;
      }

      result.push(currentHeader);
      if (!isCollapsed) {
        result.push(...visibleItems);
      }

      currentHeader = null;
      currentItems = [];
    };

    sortedListItems.forEach((item) => {
      if (isGroupHeaderItem(item)) {
        flushGroup();
        currentHeader = item;
        return;
      }

      if (!currentHeader) {
        if (
          !normalizedQuery ||
          item.name.toLowerCase().includes(normalizedQuery) ||
          item.subtitle?.toLowerCase().includes(normalizedQuery)
        ) {
          result.push(item);
        }
        return;
      }

      currentItems.push(item);
    });

    flushGroup();
    return result;
  }, [collapsedGroupIds, flyoutStructure, query, sortedListItems]);

  const groupHeaderIds = useMemo(
    () =>
      sortedListItems
        .filter((item) => isGroupHeaderItem(item))
        .map((item) => getGroupId(item))
        .filter((groupId): groupId is string => !!groupId),
    [sortedListItems],
  );

  const groupedRenderHints = useMemo(() => {
    if (flyoutStructure !== "grouped" || !getGroupedRenderHints) {
      return {} as GroupedRenderHints;
    }

    return (
      getGroupedRenderHints({
        groupHeaderIds,
        collapsedGroupIds,
        query,
      }) ?? {}
    );
  }, [
    collapsedGroupIds,
    flyoutStructure,
    getGroupedRenderHints,
    groupHeaderIds,
    query,
  ]);

  useEffect(() => {
    if (flyoutStructure !== "grouped") {
      if (collapsedGroupIds.length > 0) {
        setCollapsedGroupIds([]);
      }
      return;
    }

    const groupIds = groupHeaderIds;

    if (groupIds.length === 0) {
      if (collapsedGroupIds.length > 0) {
        setCollapsedGroupIds([]);
      }
      return;
    }

    setCollapsedGroupIds((previous) => {
      const validIds = new Set(groupIds);
      const next = previous.filter((id) => validIds.has(id));

      if (next.length > 0 || previous.length > 0) {
        return next;
      }

      const hintedDefaults = (
        groupedRenderHints.defaultCollapsedGroupIds ?? []
      ).filter((id) => validIds.has(id));

      if (hintedDefaults.length > 0) {
        return hintedDefaults;
      }

      // Fallback: expand first two sections, collapse the rest.
      return groupIds.slice(2);
    });
  }, [
    collapsedGroupIds.length,
    flyoutStructure,
    groupHeaderIds,
    groupedRenderHints.defaultCollapsedGroupIds,
  ]);

  // useLayoutEffect so collapsed defaults are applied before the browser paints, preventing flicker.
  useLayoutEffect(() => {
    if (!isOpen) {
      wasOpenRef.current = false;
      initialCollapseAppliedRef.current = false;
      return;
    }

    // Allow re-entry until initial collapse has been applied (data may arrive after flyout opens).
    if (wasOpenRef.current && initialCollapseAppliedRef.current) {
      return;
    }

    wasOpenRef.current = true;

    if (flyoutStructure !== "grouped" || groupHeaderIds.length === 0) {
      return;
    }

    initialCollapseAppliedRef.current = true;

    const hintedDefaults = (groupedRenderHints.defaultCollapsedGroupIds ?? []).filter(
      (id) => groupHeaderIds.includes(id),
    );

    if (hintedDefaults.length > 0) {
      setCollapsedGroupIds(hintedDefaults);
      return;
    }

    setCollapsedGroupIds(groupHeaderIds.slice(2));
  }, [groupHeaderIds, groupedRenderHints.defaultCollapsedGroupIds, isOpen, flyoutStructure]);

  const toggleGroupCollapse = useCallback(
    (groupId: string) => {
      const isCollapsed = collapsedGroupIds.includes(groupId);
      const collapseOthersOnExpand = groupedRenderHints.collapseOthersOnExpand ?? true;

      if (isCollapsed) {
        if (collapseOthersOnExpand) {
          // Accordion behavior: opening one section collapses all others.
          setCollapsedGroupIds(groupHeaderIds.filter((id) => id !== groupId));
          return;
        }

        setCollapsedGroupIds((previous) => previous.filter((id) => id !== groupId));
        return;
      }

      // Collapse this section while preserving collapse state of others.
      setCollapsedGroupIds((previous) =>
        previous.includes(groupId) ? previous : [...previous, groupId],
      );
    },
    [collapsedGroupIds, groupHeaderIds, groupedRenderHints.collapseOthersOnExpand],
  );

  const collectedSearchItems = useMemo(
    () => groupedListItems.filter((item) => !item.isAggregate).map((item) => item.name),
    [groupedListItems],
  );

  const visibleListItems = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    if (!normalizedQuery) {
      return groupedListItems;
    }

    return groupedListItems.filter(
      (item) =>
        item.isAggregate ||
        item.name.toLowerCase().includes(normalizedQuery) ||
        item.subtitle?.toLowerCase().includes(normalizedQuery),
    );
  }, [groupedListItems, query]);

  const pinnedFooterGroupIds = useMemo(() => {
    if (flyoutStructure !== "grouped" || query.trim().length > 0) {
      return [] as string[];
    }

    // For short lists, keep collapsed section headers inline instead of pinning
    // them to the bottom; this avoids a large visual gap in sparse states.
    const minRowsForPinnedFooter = 8;
    const visibleRowCount = visibleListItems.reduce(
      (count, item) => (item.isAggregate ? count : count + 1),
      0,
    );
    if (visibleRowCount < minRowsForPinnedFooter) {
      return [] as string[];
    }

    const hintedPinned = groupedRenderHints.pinnedFooterGroupIds ?? [];
    if (hintedPinned.length === 0) {
      return [] as string[];
    }

    const validGroupIds = new Set(groupHeaderIds);
    return hintedPinned.filter(
      (groupId) => validGroupIds.has(groupId) && collapsedGroupIds.includes(groupId),
    );
  }, [
    collapsedGroupIds,
    flyoutStructure,
    groupHeaderIds,
    groupedRenderHints.pinnedFooterGroupIds,
    query,
    visibleListItems,
  ]);

  const collapsedFooterHeaderItems = useMemo(() => {
    if (pinnedFooterGroupIds.length === 0) {
      return [] as DockFlyoutListItem[];
    }

    const pinnedIdSet = new Set(pinnedFooterGroupIds);
    return visibleListItems.filter(
      (item) =>
        isGroupHeaderItem(item) &&
        !!getGroupId(item) &&
        pinnedIdSet.has(getGroupId(item) as string),
    );
  }, [pinnedFooterGroupIds, visibleListItems]);

  const visibleListItemsForRender = useMemo(() => {
    if (collapsedFooterHeaderItems.length === 0) {
      return visibleListItems;
    }

    const footerIds = new Set(collapsedFooterHeaderItems.map((item) => item.id));
    return visibleListItems.filter((item) => !footerIds.has(item.id));
  }, [collapsedFooterHeaderItems, visibleListItems]);

  const visibleItemsMap = useMemo(
    () => new Map(visibleListItemsForRender.map((item) => [item.id, item])),
    [visibleListItemsForRender],
  );

  const navLinkItems = useMemo(
    (): NavLinkType[] =>
      visibleListItemsForRender.map((item) => ({
        key: item.id,
        label: item.name,
        leaf: true,
        data: {
          description: item.subtitle,
        },
      })),
    [visibleListItemsForRender],
  );

  const handleNavigate = (item: DockFlyoutListItem) => {
    closeRowOptionsMenu();
    navigateToItem(item);
  };

  const renderNavLinkNode = useCallback(
    (node: TreeNode): React.ReactNode => {
      const nodeKey = String(node.key ?? "");
      const item = visibleItemsMap.get(nodeKey);

      if (!item) {
        return (
          <div
            className="dock-flyout__nav-row"
            data-navlink-row="true"
            data-navlink-key={nodeKey}
          >
            <div className="dock-flyout__nav-row-main">
              <span className="dock-flyout__nav-row-label">
                {String(node.label ?? "")}
              </span>
            </div>
          </div>
        );
      }

      if (item.isAggregate) {
        const groupId = getGroupId(item) ?? item.id;
        const isCollapsed = collapsedGroupIds.includes(groupId);

        return (
          <button
            type="button"
            className="dock-flyout__group-label"
            data-navlink-row="true"
            data-navlink-key={item.id}
            aria-label={`Toggle ${item.name}`}
            aria-expanded={!isCollapsed}
            onClick={(event) => {
              event.stopPropagation();
              toggleGroupCollapse(groupId);
            }}
          >
            <span className="dock-flyout__group-label-text">{item.name}</span>
            <span className="dock-flyout__group-toggle" aria-hidden="true">
              <span className="material-symbols-outlined" aria-hidden="true">
                {isCollapsed ? "chevron_right" : "expand_more"}
              </span>
            </span>
          </button>
        );
      }

      const isPinned = isPinnedItem(item, pinnedIds, pinOverrides);
      const isUnread = isUnreadItem(item);
      const hasOptions = hasRowOptions(item);
      const isSelected = selectedIds.includes(item.id);

      return (
        <div
          className={`dock-flyout__nav-row${isPinned ? " dock-flyout__nav-row--pinned" : ""
            }${isUnread ? " dock-flyout__nav-row--unread" : ""}${isSelected ? " dock-flyout__nav-row--selected" : ""}`}
          data-navlink-row="true"
          data-navlink-key={item.id}
          aria-selected={isSelected}
          data-dock-item-pinned={isPinned ? "true" : "false"}
          data-dock-item-unread={isUnread ? "true" : "false"}
        >
          {/* {selectionActive && (
            <input
              type="checkbox"
              className="dock-flyout__nav-row-checkbox"
              aria-label={`Select ${item.name}`}
              checked={isSelected}
              onClick={(event) => event.stopPropagation()}
              onChange={() => toggleSelection?.(item.id)}
            />
          )} */}
          <div className="dock-flyout__nav-row-main">
            <span className="dock-flyout__nav-row-label">{item.name}</span>

            {item.subtitle && (
              <span className="dock-flyout__nav-row-description">
                {item.subtitle}
              </span>
            )}
          </div>

          <div className="dock-flyout__nav-row-right">
            {isPinned && (
              <span
                className="dock-flyout__pin-indicator dock-flyout__pin-indicator--filled"
                title="Pinned"
                aria-label="Pinned"
              >
                <span className="material-symbols-outlined" aria-hidden="true">
                  push_pin
                </span>
              </span>
            )}

            {isUnread && (
              <span
                className="dock-flyout__unread-dot"
                title="Unread"
                aria-label="Unread"
              />
            )}

            {hasOptions && (
              <Button
                buttonPrefix
                buttonType="Tertiary"
                classType="Standard"
                onClick={(event) => {
                  event.stopPropagation();
                  openRowOptionsMenu(item);
                }}
                prefixInstance="Kebab Menu Vertical"
                size="Medium"
                state="Default"
                theme="Rectangular"
                aria-label={`More actions for ${item.name}`}
                buttonClass="dock-flyout__row-options-trigger"
              />
            )}
          </div>
        </div>
      );
    },
    [
      openRowOptionsMenu,
      pinnedIds,
      pinOverrides,
      rowOptionsItem,
      selectedIds,
      toggleSelection,
      visibleItemsMap,
      collapsedGroupIds,
      toggleGroupCollapse,
    ],
  );

  const syncSearchFiltersToController = (filters: AppliedFilter[]) => {
    const nextFiltersBySectionId = new Map<string, string[]>();

    filterSections.forEach((section) => {
      const updatedFilter = filters.find(
        (filter) => filter.label === section.label,
      );

      nextFiltersBySectionId.set(section.id, updatedFilter?.value ?? []);
    });

    resetDraftFilters();

    filterSections.forEach((section) => {
      const currentValues = appliedFilters[section.id] ?? [];
      const nextValues = nextFiltersBySectionId.get(section.id) ?? [];

      const currentSet = new Set(currentValues);
      const nextSet = new Set(nextValues);

      currentValues.forEach((value) => {
        if (!nextSet.has(value)) {
          toggleFilterValue(section.id, value);
        }
      });

      nextValues.forEach((value) => {
        if (!currentSet.has(value)) {
          toggleFilterValue(section.id, value);
        }
      });
    });

    applyDraftFilters();
  };

  const filterButton = (
    <span className="dock-flyout__filter-trigger-btn">
      <Button
        buttonPrefix
        buttonType="Tertiary"
        classType="Standard"
        prefixInstance="Filter"
        size="Medium"
        state="Default"
        aria-label="Open filters"
      />
    </span>
  );

  const toggleSearchFilterPanel = () => {
    const trigger = searchHostRef.current?.querySelector(
      ".dock-flyout__filter-trigger-btn",
    ) as HTMLElement | null;

    if (!trigger) {
      return;
    }

    const filterbtnDiv = trigger.parentElement as HTMLElement | null;

    if (filterbtnDiv && filterIconBtnRef.current) {
      const iconRect = filterIconBtnRef.current.getBoundingClientRect();

      filterbtnDiv.style.position = "fixed";
      filterbtnDiv.style.top = `${iconRect.top}px`;
      filterbtnDiv.style.left = `${iconRect.left}px`;
      filterbtnDiv.style.width = `${iconRect.width}px`;
      filterbtnDiv.style.height = `${iconRect.height}px`;
      filterbtnDiv.style.opacity = "0";
      filterbtnDiv.style.pointerEvents = "none";
      filterbtnDiv.style.zIndex = "-1";
    }

    trigger.click();
  };

  const renderRowOptionsMenu = () => {
    if (!rowOptionsItem) {
      return null;
    }

    return (
      <div
        ref={rowOptionsMenuRef}
        className="dock-flyout__row-options-popover"
        role="menu"
        style={rowOptionsMenuStyle}
        onClick={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
      >
        {rowOptionsActions.length === 0 && (
          <div className="dock-flyout__row-options-empty">No actions</div>
        )}

        {rowOptionsActions.map((rowAction) => {
          const displayAction = getDisplayRowAction(
            rowAction,
            rowOptionsItem,
            pinnedIds,
            pinOverrides,
          );
          const rowActionId = getRowActionId(displayAction);

          return (
            <Button
              key={rowActionId}
              buttonPrefix
              text
              label={getRowActionLabel(displayAction)}
              buttonType="Tertiary"
              classType="Standard"
              prefixInstance={getRowActionIcon(displayAction)}
              size="Small"
              state="Default"
              theme="Rectangular"
              buttonClass={`dock-flyout__row-options-popover-item${rowActionId === "delete"
                ? " dock-flyout__row-options-popover-item--destructive"
                : ""
                }`}
              onClick={(event) => {
                event.stopPropagation();
                handleRowActionClick(displayAction, rowOptionsItem);
              }}
            />
          );
        })}
      </div>
    );
  };

  const deleteDialogState = useMemo(() => {
    if (!confirm) {
      return null;
    }

    if (confirm.kind === "bulk-unpinned") {
      return {
        title: "Delete All Confirmation",
        subtitle:
          "This will delete all unpinned chat sessions in Chat History, including the current conversation if it is unpinned. Are you sure you want to proceed?",
        contextLabel: "History",
        cancelLabel: "No",
        confirmLabel: "Yes",
      };
    }

    if (confirm.kind === "single") {
      return {
        title: "Delete chat",
        subtitle: "This action cannot be undone.",
        cancelLabel: "Cancel",
        confirmLabel: "Delete",
      };
    }

    if (confirm.kind === "multi-delete") {
      const count = selectedIds.length;
      return {
        title: count > 1 ? `Delete ${count} chats` : "Delete selected chat",
        subtitle: "This action cannot be undone.",
        cancelLabel: "Cancel",
        confirmLabel: "Delete",
      };
    }
  }, [confirm, listItems, pinnedIds, selectedIds.length]);

  const canSubmitRename =
    !!renameDialog?.draftName.trim() &&
    renameDialog.draftName.trim() !== renameDialog.currentName.trim();

  if (!isOpen || !flyoutConfig) {
    return null;
  }

  const headerActions = flyoutConfig.headerActions ?? [];
  const baseListIsEmpty = listItems.length === 0;
  const visibleListIsEmpty =
    visibleListItemsForRender.length === 0 && collapsedFooterHeaderItems.length === 0;
  const hasSearch = query.trim().length > 0;
  const hasScopeFilter = selectedScope !== "all";
  const hasAppliedFilters = Object.values(appliedFilters).some(
    (values) => (values?.length ?? 0) > 0,
  );

  const isTrueNoRecordsState =
    !isLoading &&
    baseListIsEmpty &&
    !hasSearch &&
    !hasScopeFilter &&
    !hasAppliedFilters;

  const hasVisibleActionableChatRows = visibleListItemsForRender.some(
    (item) => !item.isAggregate,
  );

  return ReactDOM.createPortal(
    <>
      <div
        ref={panelRef}
        className={`dock-flyout${flyoutStructure === "grouped" ? " dock-flyout--grouped" : ""}`}
        role="dialog"
        aria-label={flyoutTitle}
        style={positionStyle}
      >
        <div className="dock-flyout__header">
          <h3 className="dock-flyout__title">{flyoutTitle}</h3>

          {headerActions.length > 0 && (
            <div className="dock-flyout__header-actions">
              {headerActions.map((action) => {
                if (action.variant === "dropdown") {
                  const isChatBulkActions =
                    flyoutKind === "chats" && action.id === "chat-bulk-actions";
                  const disableChatBulkActions =
                    isChatBulkActions && !isLoading && !hasVisibleActionableChatRows;

                  return (
                    <Dropdown
                      key={action.id}
                      options={(action.options ?? []).map((option) => ({
                        id: option.id,
                        label: option.label,
                        filterText: option.label,
                      }))}
                      dropdownClass="dock-flyout__header-action-btn"
                      dropdownPanelClass="dock-flyout__menu"
                      dropdownAppendTo="self"
                      dropdownProps={{ disabled: disableChatBulkActions }}
                      onSelect={(option) =>
                        executeHeaderAction(action, option.id)
                      }
                    />
                  );
                }

                return (
                  <Button
                    key={action.id}
                    buttonPrefix
                    prefixInstance="Add"
                    buttonType="Tertiary"
                    classType="Standard"
                    size="Medium"
                    state="Default"
                    title={action.tooltip}
                    aria-label={action.tooltip ?? action.id}
                    text
                    label={action.tooltip ?? action.id}
                    onClick={() => executeHeaderAction(action)}
                    buttonClass={`dock-flyout__header-action-btn${action.tooltip
                      ? " dock-flyout__header-action-btn--with-label"
                      : ""
                      }`}
                  />
                );
              })}
            </div>
          )}
        </div>

        {!isTrueNoRecordsState &&
          (flyoutConfig.isSearchVisible ||
            flyoutConfig.isDropdownFilterVisible ||
            flyoutConfig.isFilterIconVisible) && (
            <div className="dock-flyout__toolbar">
              {flyoutConfig.isSearchVisible && (
                <div className="dock-flyout__searchbar" ref={searchHostRef}>
                  <Search
                    className="dock-flyout__search-input search-input"
                    placeholder={flyoutConfig.searchPlaceholder ?? "Search"}
                    behavior="Dynamic Suggestions"
                    filter={!!flyoutConfig.isFilterIconVisible}
                    filterState="Full Width"
                    items={collectedSearchItems}
                    filters={[]}
                    fullWidthFilters={
                      flyoutConfig.isFilterIconVisible ? searchFilters : []
                    }
                    filterButtons={filterButton}
                    useFixedPosition={true}
                    onQueryChange={(value) => {
                      if (value.trim().length > 0) {
                        setQuery(value);
                      } else {
                        clearQuery();
                      }
                    }}
                    onSelectionChange={(values) => {
                      const latestValue = values[values.length - 1] ?? "";

                      if (latestValue) {
                        setQuery(latestValue);
                      } else {
                        clearQuery();
                      }
                    }}
                    onFilterChange={syncSearchFiltersToController}
                    dropdownAppendTo="self"
                  />
                </div>
              )}

              {(flyoutConfig.isDropdownFilterVisible ||
                flyoutConfig.isFilterIconVisible) && (
                  <div className="dock-flyout__actions">
                    {flyoutConfig.isDropdownFilterVisible &&
                      dropdownOptions.length > 0 && (
                        <Dropdown
                          options={dropdownOptions.map((option) => ({
                            id: option.id,
                            label: option.label,
                            filterText: option.label,
                          }))}
                          defaultValue={selectedScope}
                          hasFavorites
                          dropdownClass="dock-flyout__ghost-trigger dock-flyout__scope-dropdown"
                          dropdownPanelClass="dock-flyout__menu dock-flyout__menu--scope dock-flyout__scope-menu"
                          dropdownAppendTo="self"
                          onSelect={(option) => setSelectedScope(option.id)}
                        />
                      )}

                    {flyoutConfig.isFilterIconVisible &&
                      flyoutConfig.isSearchVisible && (
                        <span
                          ref={filterIconBtnRef}
                          className="dock-flyout__icon-btn-wrapper"
                        >
                          <Button
                            buttonPrefix
                            buttonType="Tertiary"
                            classType="Standard"
                            prefixInstance="Filter"
                            size="Medium"
                            state="Default"
                            aria-label="Open filters"
                            onClick={toggleSearchFilterPanel}
                            buttonClass="dock-flyout__icon-btn"
                          />
                        </span>
                      )}
                  </div>
                )}
            </div>
          )}

        <div
          ref={bodyRef}
          className={`dock-flyout__body${collapsedFooterHeaderItems.length > 0 ? " dock-flyout__body--with-sticky-footer" : ""}`}
          style={
            collapsedFooterHeaderItems.length > 0
              ? ({ ["--dock-footer-count" as string]: String(collapsedFooterHeaderItems.length) } as CSSProperties)
              : undefined
          }
        >
          {isLoading && (
            <div className="dock-flyout__empty">
              <span>{flyoutConfig.loadingText ?? "Loading..."}</span>
            </div>
          )}

          {!isLoading && visibleListIsEmpty && (
            <div className="dock-flyout__empty">
              <span>{flyoutConfig.emptyStateText ?? "No data"}</span>
            </div>
          )}

          {!isLoading && !visibleListIsEmpty && (
            <div className="dock-flyout__list">
              <NavLink
                items={navLinkItems}
                showIcon={false}
                showSubNav={false}
                showNotification={false}
                treeProps={{
                  selectionMode: "single",
                  nodeTemplate: renderNavLinkNode,
                  onSelect: (event) => {
                    const item = visibleItemsMap.get(String(event.node.key));

                    if (!item || item.isAggregate) {
                      return;
                    }

                    const originalEvent = event.originalEvent as unknown as MouseEvent | undefined;

                    if (originalEvent?.ctrlKey || originalEvent?.metaKey) {
                      originalEvent.preventDefault();
                      originalEvent.stopPropagation();
                      toggleSelection?.(item.id);
                      return;
                    }

                    if (originalEvent?.shiftKey) {
                      originalEvent.preventDefault();
                      originalEvent.stopPropagation();
                      extendSelectionTo?.(item.id);
                      return;
                    }

                    if (
                      item.opensSecondary &&
                      item.navigationMode === "secondary"
                    ) {
                      const target =
                        (event.originalEvent?.target as HTMLElement | null)
                          ?.closest<HTMLElement>(
                            '[data-pc-section="content"]',
                          ) ??
                        (event.originalEvent?.target as HTMLElement | null);

                      setIsSecondaryLocked(false);
                      closeRowOptionsMenu();

                      openSecondaryForRow(
                        item.id,
                        target?.getBoundingClientRect() ?? null,
                      );
                    } else {
                      handleNavigate(item);
                    }
                  },
                }}
              />

              {isLoadingMore && (
                <div className="dock-flyout__empty">
                  <span>{flyoutConfig.loadingMoreText ?? "Loading more..."}</span>
                </div>
              )}
            </div>
          )}

          {!isLoading && collapsedFooterHeaderItems.length > 0 && (
            <div className="dock-flyout__sticky-footer-stack">
              {collapsedFooterHeaderItems.map((groupHeaderItem) => {
                const groupId = getGroupId(groupHeaderItem) ?? "";
                const isCollapsed = collapsedGroupIds.includes(groupId);

                return (
                  <button
                    key={groupHeaderItem.id}
                    type="button"
                    className="dock-flyout__sticky-footer-group"
                    aria-label={`Toggle ${groupHeaderItem.name}`}
                    aria-expanded={!isCollapsed}
                    onClick={(event) => {
                      event.stopPropagation();
                      if (groupId) {
                        toggleGroupCollapse(groupId);
                      }
                    }}
                  >
                    <span className="dock-flyout__group-label-text">{groupHeaderItem.name}</span>
                    <span className="dock-flyout__group-toggle" aria-hidden="true">
                      <span className="material-symbols-outlined" aria-hidden="true">
                        {isCollapsed ? "chevron_right" : "expand_more"}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {flyoutKind === "chats" && selectedIds.length > 0 && (
            <div className="dock-flyout__bulk-action-bar" role="toolbar" aria-label="Bulk chat actions">
              <div className="dock-flyout__bulk-action-left">
                <span className="dock-flyout__selection-badge">{selectedIds.length}</span>
                <span className="dock-flyout__selection-text" aria-live="polite">Selected</span>
              </div>
              <div className="dock-flyout__bulk-action-buttons">
                <button
                  type="button"
                  className="dock-flyout__bulk-action"
                  disabled={!exportSelection}
                  onClick={() => exportSelection?.()}>
                  <span className="material-symbols-outlined" aria-hidden="true">download</span>
                  <span>Export</span>
                </button>
                <button
                  type="button"
                  className="dock-flyout__bulk-action dock-flyout__bulk-action--delete"
                  onClick={() => openConfirm?.({ kind: "multi-delete" })}
                >
                  <span className="material-symbols-outlined" aria-hidden="true">delete</span>
                  <span>Delete</span>
                </button>
              </div>
              <button
                type="button"
                className="dock-flyout__bulk-action-close"
                aria-label="Clear chat selection"
                disabled={!clearSelection} onClick={() => clearSelection?.()}>
                <span className="material-symbols-outlined" aria-hidden="true">close</span>
              </button>
            </div>
          )}

        </div>

        {deleteDialogState && (
          <div
            className="dock-flyout__confirm-overlay"
            role="presentation"
            onMouseDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
          >
            <div
              className="dock-flyout__confirm-dialog"
              role="dialog"
              aria-modal="true"
              aria-label={deleteDialogState.title}
            >
              {deleteDialogState.contextLabel && (
                <span className="dock-flyout__confirm-context-label">
                  {deleteDialogState.contextLabel}
                </span>
              )}
              {confirm?.kind === "bulk-unpinned" && (
                <Button
                  buttonPrefix
                  buttonType="Tertiary"
                  classType="Standard"
                  prefixInstance="Close"
                  size="Medium"
                  state="Default"
                  theme="Rectangular"
                  aria-label="Close confirmation"
                  title="Close"
                  buttonClass="dock-flyout__confirm-close-btn"
                  onClick={() => closeConfirm?.()}
                />
              )}
              <h4 className="dock-flyout__confirm-title">{deleteDialogState.title}</h4>
              {deleteDialogState.subtitle && (
                <p className="dock-flyout__confirm-subtitle">{deleteDialogState.subtitle}</p>
              )}

              <div className="dock-flyout__confirm-actions">
                <Button
                  text
                  label={deleteDialogState.cancelLabel}
                  buttonType="Tertiary"
                  classType="Standard"
                  size="Medium"
                  state="Default"
                  theme="Rectangular"
                  onClick={() => closeConfirm?.()}
                />
                <Button
                  text
                  label={deleteDialogState.confirmLabel}
                  buttonType="Primary"
                  classType="Standard"
                  size="Medium"
                  state="Default"
                  theme="Rectangular"
                  onClick={() => confirmDelete?.()}
                />
              </div>
            </div>
          </div>
        )}

        {renameDialog && (
          <div
            className="dock-flyout__rename-overlay"
            role="presentation"
            onMouseDown={(event) => event.stopPropagation()}
            onClick={(event) => {
              event.stopPropagation();
              closeRenameDialog?.();
            }}
          >
            <div
              className="dock-flyout__rename-dialog"
              role="dialog"
              aria-modal="true"
              aria-label="Rename chat"
              onMouseDown={(event) => event.stopPropagation()}
              onClick={(event) => event.stopPropagation()}
            >
              <h4 className="dock-flyout__rename-title">Rename chat</h4>
              <p className="dock-flyout__rename-subtitle">Enter a new name for this chat session.</p>

              <label className="dock-flyout-rename__label" htmlFor="dock-flyout-rename-input">
                Chat name
              </label>
              <input
                id="dock-flyout-rename-input"
                type="text"
                className="dock-flyout-rename__input"
                maxLength={120}
                autoFocus
                value={renameDialog.draftName}
                onChange={(event) => setRenameDraftName?.(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && canSubmitRename && !isRenaming) {
                    event.preventDefault();
                    submitRename?.();
                  }
                }}
                placeholder="Enter chat name"
              />

              <div className="dock-flyout__rename-actions">
                <Button
                  text
                  label="Cancel"
                  buttonType="Tertiary"
                  classType="Standard"
                  size="Medium"
                  state="Default"
                  theme="Rectangular"
                  onClick={() => closeRenameDialog?.()}
                />
                <Button
                  text
                  label={isRenaming ? "Saving..." : "Save"}
                  buttonType="Primary"
                  classType="Standard"
                  size="Medium"
                  state="Default"
                  theme="Rectangular"
                  disabled={isRenaming || !canSubmitRename}
                  onClick={() => submitRename?.()}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {renderRowOptionsMenu()}

      {flyoutConfig.structure === "nested" && activeSecondaryItem && (
        <DockFlyoutSecondaryPanel
          ref={secondaryPanelRef}
          item={activeSecondaryItem}
          openSecondaryGroupId={openSecondaryGroupId}
          setOpenSecondaryGroupId={setOpenSecondaryGroupId}
          positionStyle={secondaryPositionStyle}
          onNavigate={handleNavigate}
          onLockOpen={() => setIsSecondaryLocked(true)}
          onReload={syncWorkspaceObjectives}
          onMouseLeave={(event) => {
            if (isSecondaryLocked || rowOptionsItem) {
              return;
            }

            const relatedTarget = event.relatedTarget;

            if (
              relatedTarget instanceof Element &&
              (relatedTarget.closest(".dock-flyout__row-shell") ||
                relatedTarget.closest('[data-navlink-row="true"]') ||
                relatedTarget.closest(".dock-secondary") ||
                relatedTarget.closest('[data-dock-secondary-panel="true"]'))
            ) {
              return;
            }

            closeSecondary();
          }}
        />
      )}
    </>,
    document.body,
  );
};

export default DockFlyout;